import React, { useState, useEffect } from 'react'
import { 
  Box, 
  Typography, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { History, Edit } from '@mui/icons-material'
import { useRefresh, useNotify, useDataProvider } from 'react-admin'
import { supabase } from '../supabaseClient'
import { Property } from '../types'
import { formatPLN } from '@repo/domain'

interface PriceColumnProps {
  record: Property & {
    total_price?: string
    price_per_sqm?: string
    area_total?: number
  }
  originalPrice?: number
}

interface PriceVersion {
  id: string
  total_price: number | null
  currency: string | null
  valid_from: string
  valid_to?: string | null
  created_at: string | null
}

export const PriceColumn: React.FC<PriceColumnProps> = ({ record, originalPrice }) => {
  const [historyOpen, setHistoryOpen] = useState(false)
  const [changeOpen, setChangeOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [priceHistory, setPriceHistory] = useState<PriceVersion[]>([])
  const [newPrice, setNewPrice] = useState('')
  const [newPricePerSqm, setNewPricePerSqm] = useState('')
  const [priceInputMode, setPriceInputMode] = useState(0) // 0: total, 1: per sqm
  const [loading, setLoading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const refresh = useRefresh()
  const notify = useNotify()
  const dataProvider = useDataProvider()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Calculate price based on input mode
  const calculatePrice = (value: string, mode: number) => {
    if (!value || !record.area_total) return ''
    const numValue = Number(value)
    if (isNaN(numValue)) return ''
    
    if (mode === 0) {
      // Total price input - calculate per sqm
      return (numValue / record.area_total).toFixed(2)
    } else {
      // Per sqm input - calculate total
      return (numValue * record.area_total).toFixed(0)
    }
  }

  // Handle price input changes
  const handleTotalPriceChange = (value: string) => {
    setNewPrice(value)
    setNewPricePerSqm(calculatePrice(value, 0))
  }

  const handlePricePerSqmChange = (value: string) => {
    setNewPricePerSqm(value)
    setNewPrice(calculatePrice(value, 1))
  }

  const loadPriceHistory = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('property_price_versions')
        .select('*')
        .eq('property_id', record.id)
        .order('valid_from', { ascending: false })

      if (error) throw error
      setPriceHistory(data || [])
    } catch (error) {
      console.error('Error loading price history:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleHistoryClick = () => {
    setHistoryOpen(true)
    loadPriceHistory()
  }

  const handleChangePriceClick = () => {
    setNewPrice(record.total_price || '')
    setNewPricePerSqm(record.price_per_sqm || '')
    setPriceInputMode(0)
    setChangeOpen(true)
  }

  const handlePriceChangeConfirm = () => {
    setChangeOpen(false)
    setConfirmOpen(true)
  }

  const handlePriceUpdate = async () => {
    try {
      setLoading(true)

      // Use RPC function to update price
      const { data, error } = await supabase.rpc('update_property_price', {
        p_property_id: record.id,
        p_new_price: Number(newPrice)
      })

      if (error) throw error

      if (data?.success) {
        setConfirmOpen(false)
        setNewPrice('')
        setNewPricePerSqm('')

        // Force refresh to ensure data is current after a short delay
        setTimeout(() => {
          refresh()
          setRefreshKey(prev => prev + 1)
        }, 100)

        notify('Cena została zaktualizowana', { type: 'success' })
      } else {
        throw new Error('RPC returned unsuccessful result')
      }
    } catch (error) {
      console.error('Error updating price:', error)
      notify('Błąd podczas aktualizacji ceny', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  if (!record.total_price) {
    return <Typography variant="body2" color="text.secondary">-</Typography>
  }

  return (
    <Box>
      {/* Main price display */}
      <Box display="flex" alignItems="center" gap={1}>
        <Box>
          {originalPrice != null && (
            <Typography variant="body2" color="text.secondary" fontSize="0.75rem" sx={{ textDecoration: 'line-through' }}>
              {formatPLN(originalPrice)}
            </Typography>
          )}
          <Typography variant="body1" fontWeight="medium">
            {formatPLN(Number(record.total_price))}
          </Typography>
          {originalPrice != null && record.area_total && record.area_total > 0 && (
            <Typography variant="body2" color="text.secondary" fontSize="0.75rem" sx={{ textDecoration: 'line-through' }}>
              {formatPLN(Math.round(originalPrice / record.area_total))}/m²
            </Typography>
          )}
          {record.price_per_sqm && (
            <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
              {formatPLN(Number(record.price_per_sqm))}/m²
            </Typography>
          )}
        </Box>
        
        {/* Action buttons */}
        <Box display="flex" flexDirection="column">
          <Tooltip title="Historia cen">
            <IconButton size="small" onClick={handleHistoryClick}>
              <History fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zmień cenę">
            <IconButton size="small" onClick={handleChangePriceClick}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Price History Dialog */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth={isMobile ? false : "md"}
        fullWidth={!isMobile}
        fullScreen={isMobile}
      >
        <DialogTitle>Historia cen - {record.title}</DialogTitle>
        <DialogContent>
          {loading ? (
            <Typography>Ładowanie...</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cena</TableCell>
                    <TableCell>Waluta</TableCell>
                    <TableCell>Od</TableCell>
                    <TableCell>Do</TableCell>
                    <TableCell>Utworzono</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {priceHistory.map((version) => (
                    <TableRow key={version.id}>
                      <TableCell>{formatPLN(Number(version.total_price || 0))}</TableCell>
                      <TableCell>{version.currency || 'PLN'}</TableCell>
                      <TableCell>
                        {new Date(version.valid_from).toLocaleDateString('pl-PL')}
                      </TableCell>
                      <TableCell>
                        {version.valid_to 
                          ? new Date(version.valid_to).toLocaleDateString('pl-PL')
                          : 'Aktualna'
                        }
                      </TableCell>
                      <TableCell>
                        {version.created_at ? new Date(version.created_at).toLocaleDateString('pl-PL') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Zamknij</Button>
        </DialogActions>
      </Dialog>

      {/* Change Price Dialog */}
      <Dialog
        open={changeOpen}
        onClose={() => setChangeOpen(false)}
        maxWidth={isMobile ? false : "sm"}
        fullWidth={!isMobile}
        fullScreen={isMobile}
      >
        <DialogTitle>Zmiana ceny - {record.title}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Tabs 
              value={priceInputMode} 
              onChange={(_, newValue) => setPriceInputMode(newValue)}
              sx={{ mb: 2 }}
            >
              <Tab label="Cena całkowita" />
              <Tab label="Cena za m²" />
            </Tabs>

            {priceInputMode === 0 ? (
              <TextField
                label="Cena całkowita (PLN)"
                value={newPrice}
                onChange={(e) => handleTotalPriceChange(e.target.value)}
                fullWidth
                type="number"
                inputProps={{ step: 1, min: 0 }}
              />
            ) : (
              <TextField
                label="Cena za m² (PLN)"
                value={newPricePerSqm}
                onChange={(e) => handlePricePerSqmChange(e.target.value)}
                fullWidth
                type="number"
                inputProps={{ step: 0.01, min: 0 }}
              />
            )}

            {/* Preview calculated values */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Podgląd:
              </Typography>
              <Typography variant="body2">
                Cena całkowita: {newPrice ? formatPLN(Number(newPrice)) : '-'}
              </Typography>
              <Typography variant="body2">
                Cena za m²: {newPricePerSqm ? formatPLN(Number(newPricePerSqm)) + '/m²' : '-'}
              </Typography>
              {record.area_total && (
                <Typography variant="body2">
                  Powierzchnia: {record.area_total} m²
                </Typography>
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Aktualna cena: {formatPLN(Number(record.total_price || 0))}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeOpen(false)}>Anuluj</Button>
          <Button 
            onClick={handlePriceChangeConfirm}
            variant="contained"
            disabled={!newPrice || newPrice === record.total_price}
          >
            Zmień cenę
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth={isMobile ? false : "sm"}
        fullScreen={isMobile && false} // Confirmation dialog nie powinien być fullScreen
      >
        <DialogTitle>Potwierdź zmianę ceny</DialogTitle>
        <DialogContent>
          <Typography>
            Czy na pewno chcesz zmienić cenę z {formatPLN(Number(record.total_price || 0))} na {formatPLN(Number(newPrice))}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Ta operacja zamknie aktualną cenę i utworzy nową wersję.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Anuluj</Button>
          <Button 
            onClick={handlePriceUpdate}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? 'Zapisywanie...' : 'Potwierdź'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}